'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import { useAuth } from '@/contexts/AuthContext';

interface AppLayoutProps {
  children: React.ReactNode;
  role: 'client' | 'admin';
  user?: any;
}

export default function AppLayout({ children, role, user }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { signOut } = useAuth();
  const router = useRouter();

  const handleSignOut = async () => {
    try {
      await signOut();
      router.replace('/login');
    } catch {
      router.replace('/login');
    }
  };

  const initials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : role === 'admin' ? 'MA' : 'SH';

  const displayName = user?.user_metadata?.full_name || (role === 'admin' ? 'Marcus Aldridge' : 'Client');
  const displayRole = role === 'admin' ? 'Owner' : 'Client';

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-30 lg:hidden fade-in"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        role={role}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        displayName={displayName}
        displayRole={displayRole}
        initials={initials}
      />

      {/* Main content */}
      <div
        className="flex-1 flex flex-col min-w-0 overflow-hidden content-transition"
        style={{ marginLeft: 0 }}
      >
        {/* Topbar */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-6 flex-shrink-0 z-20">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="lg:hidden btn-ghost p-2 -ml-2"
            aria-label="Open navigation"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <div className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono-data text-xs bg-muted px-2 py-1 rounded-md">
              {role === 'admin' ? 'Admin Portal' : 'Client Portal'}
            </span>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            {/* Notification bell */}
            <button className="btn-ghost p-2 relative" aria-label="Notifications">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
              </svg>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-negative" />
            </button>

            {/* Role switcher demo */}
            <div className="hidden md:flex items-center gap-1 bg-muted rounded-lg p-1">
              <a
                href="/"
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 ${
                  role === 'client' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Client
              </a>
              <a
                href="/admin-dashboard"
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 ${
                  role === 'admin' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Admin
              </a>
            </div>

            {/* Avatar */}
            <button
              onClick={handleSignOut}
              className="btn-ghost p-2"
              aria-label="Sign out"
              title="Sign out"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
            </button>

            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold flex-shrink-0">
              {initials}
            </div>
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          {children}
        </main>
      </div>
    </div>
  );
}